/**
 * Maps playbook token intents (th.*, T.*) to production CSS variables for copy/export.
 */
import { T } from "./tokens";

/** @typedef {{ token: string, cssVar?: string, theme?: "dark"|"light"|"both" }} TokenExportEntry */

/** @type {Record<string, TokenExportEntry>} */
export const TOKEN_EXPORT_MAP = {
  "th.bg": { token: "th.bg", cssVar: "--zh-d-canvas", theme: "dark" },
  "th.surface": { token: "th.surface", cssVar: "--zh-d-surface", theme: "dark" },
  "th.elevated": { token: "th.elevated", cssVar: "--zh-d-elevated", theme: "dark" },
  "th.muted": { token: "th.muted", cssVar: "--zh-d-muted", theme: "dark" },
  "th.border": { token: "th.border", cssVar: "--zh-d-border", theme: "dark" },
  "th.borderMd": { token: "th.borderMd", cssVar: "--zh-d-border-md", theme: "dark" },
  "th.text": { token: "th.text", cssVar: "--zh-d-text", theme: "dark" },
  "th.textSec": { token: "th.textSec", cssVar: "--zh-d-text-secondary", theme: "dark" },
  "th.textMuted": { token: "th.textMuted", cssVar: "--zh-d-text-muted", theme: "dark" },
  "th.sidebar": { token: "th.sidebar", theme: "dark" },
  "T.brand": { token: "T.brand", cssVar: "--ziniz-brand-primary", theme: "both" },
  "T.brandHover": { token: "T.brandHover", cssVar: "--ziniz-primary-action-hover", theme: "both" },
  "T.brandSoft": { token: "T.brandSoft", cssVar: "--ziniz-outline-action-hover-bg", theme: "both" },
  "T.brandBorder": { token: "T.brandBorder", theme: "both" },
  "T.cyan": { token: "T.cyan", cssVar: "--ziniz-tech-cyan", theme: "both" },
  "T.teal": { token: "T.teal", cssVar: "--ziniz-tech-teal", theme: "both" },
  "T.amber": { token: "T.amber", cssVar: "--ziniz-signal-amber", theme: "both" },
  "T.green": { token: "T.green", cssVar: "--zh-success", theme: "both" },
  "T.red": { token: "T.red", cssVar: "--zh-danger", theme: "both" },
  "T.violet": { token: "T.violet", cssVar: "--ziniz-edge-violet", theme: "both" },
  "T.dCanvas": { token: "T.dCanvas", cssVar: "--zh-d-canvas", theme: "dark" },
  "T.dSurface": { token: "T.dSurface", cssVar: "--zh-d-surface", theme: "dark" },
  "T.dElevated": { token: "T.dElevated", cssVar: "--zh-d-elevated", theme: "dark" },
  "T.dMuted": { token: "T.dMuted", cssVar: "--zh-d-muted", theme: "dark" },
  "T.dText": { token: "T.dText", cssVar: "--zh-d-text", theme: "dark" },
  "T.dTextSec": { token: "T.dTextSec", cssVar: "--zh-d-text-secondary", theme: "dark" },
  "T.dTextMuted": { token: "T.dTextMuted", cssVar: "--zh-d-text-muted", theme: "dark" },
  "T.lCanvas": { token: "T.lCanvas", theme: "light" },
  "T.lSurface": { token: "T.lSurface", theme: "light" },
  "T.lMuted": { token: "T.lMuted", theme: "light" },
  "T.lText": { token: "T.lText", theme: "light" },
  "T.lTextSec": { token: "T.lTextSec", theme: "light" },
  "T.lTextMuted": { token: "T.lTextMuted", theme: "light" },
  "T.rsm": { token: "T.rsm", theme: "both" },
  "T.rmd": { token: "T.rmd", theme: "both" },
  "T.rlg": { token: "T.rlg", theme: "both" },
  "T.rxl": { token: "T.rxl", theme: "both" },
  "T.fast": { token: "T.fast", theme: "both" },
  "T.mid": { token: "T.mid", theme: "both" },
  "T.slow": { token: "T.slow", theme: "both" },
  "T.ease": { token: "T.ease", theme: "both" },
  "T.spring": { token: "T.spring", theme: "both" },
};

const T_KEY_LOOKUP = Object.fromEntries(
  Object.entries(T).map(([key, value]) => [`T.${key}`, value])
);

/**
 * Resolve a token string (e.g. "th.surface", "T.brand") to export metadata + resolved value.
 * @param {string} tokenRef
 * @param {boolean} dark
 * @returns {{ token: string, cssVar?: string, theme: string, resolved: string }}
 */
export function resolveTokenExport(tokenRef, dark) {
  const trimmed = String(tokenRef || "").trim();
  const mapped = TOKEN_EXPORT_MAP[trimmed];
  let resolved = "";

  if (trimmed.startsWith("th.")) {
    const themeKey = trimmed.slice(3);
    const themeMap = {
      bg: dark ? T.dCanvas : T.lCanvas,
      surface: dark ? T.dSurface : T.lSurface,
      elevated: dark ? T.dElevated : T.lSurface,
      muted: dark ? T.dMuted : T.lMuted,
      border: dark ? T.dBorder : T.lBorder,
      borderMd: dark ? T.dBorderMd : T.lBorderMd,
      text: dark ? T.dText : T.lText,
      textSec: dark ? T.dTextSec : T.lTextSec,
      textMuted: dark ? T.dTextMuted : T.lTextMuted,
      sidebar: dark ? "#0d1017" : T.lSurface,
    };
    resolved = themeMap[themeKey] || trimmed;
  } else if (trimmed.startsWith("T.")) {
    resolved = T_KEY_LOOKUP[trimmed] || trimmed;
  } else {
    resolved = trimmed;
  }

  return {
    token: mapped?.token || trimmed,
    cssVar: mapped?.cssVar,
    theme: mapped?.theme || (dark ? "dark" : "light"),
    resolved: String(resolved),
  };
}

/**
 * Normalize tokens prop (string or object per CSS property) for Styles panel.
 * @param {Record<string, string|object>} tokens
 * @param {Record<string, string>} css
 * @param {boolean} dark
 */
export function enrichTokensForExport(tokens, css, dark) {
  const keys = Array.from(new Set([...Object.keys(tokens || {}), ...Object.keys(css || {})]));
  return keys.map((prop) => {
    const raw = tokens?.[prop];
    if (raw && typeof raw === "object" && raw.token) {
      const extra = resolveTokenExport(raw.token, dark);
      return {
        prop,
        ...raw,
        cssVar: raw.cssVar || extra.cssVar,
        resolved: css[prop] ?? raw.resolved ?? extra.resolved,
        theme: raw.theme || extra.theme,
      };
    }
    const tokenRef = typeof raw === "string" ? raw : "";
    const extra = tokenRef ? resolveTokenExport(tokenRef, dark) : { token: "", resolved: "" };
    return {
      prop,
      token: tokenRef || extra.token,
      cssVar: extra.cssVar,
      theme: extra.theme,
      resolved: css[prop] ?? extra.resolved ?? "",
    };
  });
}

/**
 * @param {Array<{ prop: string, token: string, cssVar?: string, theme?: string, resolved: string }>} rows
 * @param {boolean} dark
 */
export function formatStylesCopyBlock(rows, dark) {
  const themeLabel = dark ? "dark" : "light";
  const lines = [`/* theme: ${themeLabel} */`, ""];
  rows.forEach((row) => {
    if (!row.resolved && row.resolved !== 0) return;
    const value = row.cssVar ? `var(${row.cssVar})` : row.resolved;
    const comment = [
      row.token && row.token !== row.resolved ? row.token : null,
      row.theme && row.theme !== "both" ? row.theme : null,
      !row.cssVar && row.token?.startsWith("T.") ? row.token : null,
    ]
      .filter(Boolean)
      .join(" · ");
    const prop = camelToKebab(row.prop);
    lines.push(comment ? `${prop}: ${value}; /* ${comment} */` : `${prop}: ${value};`);
  });
  return lines.join("\n");
}

function camelToKebab(propertyName) {
  return propertyName.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}

/**
 * @param {object} flowContext
 */
export function formatBriefForLlm(flowContext) {
  if (!flowContext) return "";
  const {
    flowLabel,
    stepLabel,
    stepNote,
    shell,
    shellBlurb,
    devBrief = {},
  } = flowContext;

  const lines = [
    `# ${flowLabel} — ${stepLabel}`,
    `Route: ${devBrief.route || "—"} | Shell: ${shell || devBrief.shell || "—"} | Auth: ${devBrief.auth || "—"}`,
    "",
    "## Step notes",
    stepNote || "(none)",
    "",
    "## Shell",
    shell || devBrief.shell || "—",
    shellBlurb || "",
    "",
    "## Build checklist",
  ];

  if (devBrief.primaryAction) lines.push(`- Primary action: ${devBrief.primaryAction}`);
  if (devBrief.states?.length) lines.push(`- States: ${devBrief.states.join(", ")}`);
  if (devBrief.mobile) lines.push(`- Mobile: ${devBrief.mobile}`);
  if (devBrief.tierGating) lines.push(`- Tier gating: ${devBrief.tierGating}`);
  if (devBrief.edgeCases?.length) {
    devBrief.edgeCases.forEach((edgeCase) => lines.push(`- Edge case: ${edgeCase}`));
  }

  return lines.filter((line, index, arr) => !(line === "" && arr[index + 1] === "")).join("\n");
}

export function shellBlurbForName(shellName) {
  if (!shellName) return "";
  if (shellName.includes("Public")) {
    return "Split layout — brand panel left, form right. MyHub©2026 footer.";
  }
  if (shellName.includes("Cust")) {
    return "Persistent left sidebar (Discover/Orders/Profile). Top nav: dark toggle + cart drawer + auth links. Footer: Powered by ZiniHub.";
  }
  return "Dark sidebar nav + topbar (breadcrumb + hub switcher + bell + avatar). MyHub©2026 footer.";
}
